// Vorschauen für Assets, die nur auf dem Server liegen: laden (höchstens ein paar gleichzeitig), auswerten, merken. Ohne DOM.
import Skybox from '../formats/skybox.ts';
import Spr from '../formats/spr.ts';
import Wad from '../formats/wad.ts';
import type { Pixels, WadFile } from '../formats/wad.ts';

/** Verkleinert ein Bild (nächster Nachbar) auf höchstens max Pixel Kantenlänge; kleinere bleiben unverändert */
export function thumbnail(px: Pixels, max: number): Pixels {
  const s = Math.max(px.w, px.h);
  if (s <= max) return px;
  const w = Math.max(1, Math.round(px.w * max / s)), h = Math.max(1, Math.round(px.h * max / s));
  const out = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const sx = Math.min(px.w - 1, Math.floor(x * px.w / w)), sy = Math.min(px.h - 1, Math.floor(y * px.h / h));
    const si = (sy * px.w + sx) * 4, di = (y * w + x) * 4;
    out[di] = px.rgba[si]; out[di + 1] = px.rgba[si + 1]; out[di + 2] = px.rgba[si + 2]; out[di + 3] = px.rgba[si + 3];
  }
  return { w, h, rgba: out };
}

/** Führt höchstens max Aufgaben gleichzeitig aus */
export function limiter(max: number): <T>(fn: () => Promise<T>) => Promise<T> {
  let running = 0;
  const waiting: (() => void)[] = [];
  const next = () => { running--; const w = waiting.shift(); if (w) w(); };
  return async <T>(fn: () => Promise<T>): Promise<T> => {
    if (running >= max) await new Promise<void>((r) => waiting.push(r));
    running++;
    try { return await fn(); } finally { next(); }
  };
}

const SKY_ORDER = ['ft', 'rt', 'bk', 'lf', 'up', 'dn'];

export class PreviewCache {
  private bufs = new Map<string, Promise<ArrayBuffer>>();
  private pix = new Map<string, Promise<Pixels>>();
  private wads = new Map<string, Promise<WadFile>>();
  private run: ReturnType<typeof limiter>;
  private fetchBuffer: (path: string) => Promise<ArrayBuffer>;
  constructor(fetchBuffer: (path: string) => Promise<ArrayBuffer>, concurrency = 4) { this.fetchBuffer = fetchBuffer; this.run = limiter(concurrency); }

  private memo<V>(map: Map<string, Promise<V>>, key: string, make: () => Promise<V>): Promise<V> {
    let p = map.get(key);
    if (!p) { p = make(); map.set(key, p); p.catch(() => map.delete(key)); }   // Fehler nicht merken, damit ein neuer Versuch möglich bleibt
    return p;
  }
  buffer(path: string): Promise<ArrayBuffer> { return this.memo(this.bufs, path, () => this.run(() => this.fetchBuffer(path))); }
  /** erstes Bild eines Sprites */
  sprite(path: string, max = 128): Promise<Pixels> {
    return this.memo(this.pix, 'spr:' + path, async () => { const s = Spr.parse(await this.buffer(path)); return thumbnail({ w: s.w, h: s.h, rgba: s.rgba }, max); });
  }
  /** Bild einer Skybox-Seite (TGA) */
  skySide(path: string, max = 128): Promise<Pixels> {
    return this.memo(this.pix, 'sky:' + path + ':' + max, async () => thumbnail(Skybox.parseTga(await this.buffer(path)), max));
  }
  /** WAD lesen; der Puffer wird danach nicht weiter gebraucht und freigegeben */
  wad(path: string): Promise<WadFile> {
    return this.memo(this.wads, path, async () => { const w = Wad.parse(await this.buffer(path), path); this.bufs.delete(path); return w; });
  }
  clear(): void { this.bufs.clear(); this.pix.clear(); this.wads.clear(); }
}

/** Welcher Serverpfad einer Skybox am besten als Vorschau taugt (Vorderseite zuerst) */
export function pickSkyPath(paths: string[]): string | null {
  const side = (p: string) => (/(rt|lf|up|dn|ft|bk)\.[a-z]+$/i.exec(p) || [])[1]?.toLowerCase() ?? '';
  const sorted = paths.slice().sort((a, b) => SKY_ORDER.indexOf(side(a)) - SKY_ORDER.indexOf(side(b)));
  return sorted[0] ?? null;
}
